// Settings page — self-service profile editing, password change, MFA management,
// notification preferences, account actions, and app info.

import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  updatePassword,
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference,
} from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';
import { useProfile, useUpdateProfile } from '../hooks/useProfile';
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from '../hooks/useNotificationPreferences';
import { useManagedContracts } from '../hooks/useManagedContracts';
import { Card, Button, Alert } from '../components/common';
import NotificationPreferencesCard from '../components/settings/NotificationPreferencesCard';
import type { UpdateNotificationPreferences } from '../types';

type MfaState =
  | 'loading'
  | 'inactive'
  | 'active'
  | 'setting-up'
  | 'confirming-disable';

interface TotpSetup {
  sharedSecret: string;
  uri: string;
}

export default function Settings() {
  const { user, logout } = useAuth();
  const { data: profile, isLoading: profileLoading } = useProfile();
  const updateProfileMutation = useUpdateProfile();

  // Notification preferences
  const {
    data: notifPrefs,
    isLoading: notifLoading,
    isError: notifError,
  } = useNotificationPreferences();
  const updateNotifMutation = useUpdateNotificationPreferences();

  // Managed contracts — used to populate the mute picker and resolve muted titles.
  const { data: managedContractsData } = useManagedContracts();

  // Profile form state
  const [name, setName] = useState('');
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState('');

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordChanging, setPasswordChanging] = useState(false);

  // MFA state
  const [mfaState, setMfaState] = useState<MfaState>('loading');
  const [totpSetup, setTotpSetup] = useState<TotpSetup | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaSuccess, setMfaSuccess] = useState('');

  // Pattern 0: seed the name field during render when the async profile loads.
  // Guard on the profile name value so the user's in-progress edits are not
  // overwritten on every render — only when the server value changes.
  const profileName = profile?.name ?? null;
  const [prevProfileName, setPrevProfileName] = useState<string | null>(null);
  if (profileName !== null && profileName !== prevProfileName) {
    setPrevProfileName(profileName);
    setName(profileName);
  }

  // Fetch MFA preference on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pref = await fetchMFAPreference();
        if (cancelled) return;
        if (pref?.preferred === 'TOTP' || pref?.enabled?.includes('TOTP')) {
          setMfaState('active');
        } else {
          setMfaState('inactive');
        }
      } catch {
        if (!cancelled) setMfaState('inactive');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const nameUnchanged = name === (profile?.name || '');
  const nameInvalid = !name.trim();
  const profileSaveDisabled =
    nameUnchanged || nameInvalid || updateProfileMutation.isPending;

  const handleProfileSave = async () => {
    setProfileError('');
    setProfileSuccess(false);
    try {
      await updateProfileMutation.mutateAsync({ name: name.trim() });
      setProfileSuccess(true);
    } catch (error) {
      setProfileError(
        error instanceof Error ? error.message : 'Failed to update profile'
      );
    }
  };

  const handlePasswordChange = async () => {
    setPasswordError('');
    setPasswordSuccess(false);

    if (newPassword.length < 12) {
      setPasswordError('Password must be at least 12 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    setPasswordChanging(true);
    try {
      await updatePassword({ oldPassword: currentPassword, newPassword });
      setPasswordSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordError(
        error instanceof Error ? error.message : 'Failed to change password'
      );
    } finally {
      setPasswordChanging(false);
    }
  };

  const handleEnableMfa = async () => {
    setMfaError('');
    setMfaSuccess('');
    try {
      const result = await setUpTOTP();
      setTotpSetup({
        sharedSecret: result.sharedSecret,
        uri: result.getSetupUri('ContractGen').toString(),
      });
      setMfaState('setting-up');
    } catch (err) {
      setMfaError(
        err instanceof Error ? err.message : 'Failed to set up MFA'
      );
    }
  };

  const handleVerifyTotp = async () => {
    setMfaError('');
    try {
      await verifyTOTPSetup({ code: mfaCode });
      await updateMFAPreference({ totp: 'PREFERRED' });
      setMfaSuccess('MFA has been enabled successfully.');
      setMfaState('active');
      setMfaCode('');
      setTotpSetup(null);
    } catch (err) {
      setMfaError(
        err instanceof Error ? err.message : 'Verification failed'
      );
    }
  };

  const handleCancelMfaSetup = () => {
    setMfaState('inactive');
    setTotpSetup(null);
    setMfaCode('');
    setMfaError('');
  };

  const handleDisableMfa = () => {
    setMfaError('');
    setMfaSuccess('');
    setMfaState('confirming-disable');
  };

  const handleConfirmDisable = async () => {
    setMfaError('');
    try {
      await updateMFAPreference({ totp: 'DISABLED' });
      setMfaState('inactive');
      setMfaSuccess('MFA has been disabled.');
    } catch (err) {
      setMfaError(
        err instanceof Error ? err.message : 'Failed to disable MFA'
      );
    }
  };

  const handleCancelDisable = () => {
    setMfaState('active');
    setMfaError('');
  };

  // Notification preference helpers. patchPrefs restates the full current
  // prefs (PUT is full-replace on the backend) so each caller only supplies
  // the field(s) it's actually changing.
  const patchPrefs = (partial: Partial<UpdateNotificationPreferences>) => {
    if (!notifPrefs || updateNotifMutation.isPending) return;
    updateNotifMutation.mutate({
      overdueEnabled: notifPrefs.overdueEnabled,
      dueSoonEnabled: notifPrefs.dueSoonEnabled,
      insuranceEnabled: notifPrefs.insuranceEnabled,
      mutedContractIds: notifPrefs.mutedContractIds,
      ...partial,
    });
  };

  const handleToggleOverdue = () => {
    if (!notifPrefs) return;
    patchPrefs({ overdueEnabled: !notifPrefs.overdueEnabled });
  };

  const handleToggleDueSoon = () => {
    if (!notifPrefs) return;
    patchPrefs({ dueSoonEnabled: !notifPrefs.dueSoonEnabled });
  };

  const handleToggleInsurance = () => {
    if (!notifPrefs) return;
    patchPrefs({ insuranceEnabled: !notifPrefs.insuranceEnabled });
  };

  const handleRemoveMutedContract = (contractId: string) => {
    if (!notifPrefs) return;
    patchPrefs({
      mutedContractIds: notifPrefs.mutedContractIds.filter(
        (id) => id !== contractId
      ),
    });
  };

  const handleMuteContract = (contractId: string) => {
    if (!notifPrefs || !contractId) return;
    if (notifPrefs.mutedContractIds.includes(contractId)) return;
    patchPrefs({ mutedContractIds: [...notifPrefs.mutedContractIds, contractId] });
  };

  const allContracts = managedContractsData?.items ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-h1 text-slate-900 dark:text-slate-50">Settings</h1>

      {/* Profile */}
      <Card>
        <h2 className="text-h3 text-slate-900 dark:text-slate-50 mb-4">
          Profile
        </h2>
        {profileLoading ? (
          <p className="text-body-sm text-slate-500 dark:text-slate-400">
            Loading profile...
          </p>
        ) : (
          <div className="space-y-4">
            <div>
              <label htmlFor="profile-name" className="label">
                Name
              </label>
              <input
                id="profile-name"
                type="text"
                className="input mt-1"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setProfileSuccess(false);
                  setProfileError('');
                }}
                maxLength={200}
              />
            </div>
            <dl className="space-y-4">
              <div>
                <dt className="text-body-sm font-medium text-slate-500 dark:text-slate-400">
                  Email
                </dt>
                <dd className="text-body-sm text-slate-900 dark:text-slate-50">
                  {profile?.email || user?.email}
                </dd>
              </div>
              <div>
                <dt className="text-body-sm font-medium text-slate-500 dark:text-slate-400">
                  Role
                </dt>
                <dd className="text-body-sm text-slate-900 dark:text-slate-50 capitalize">
                  {profile?.role || user?.role}
                </dd>
              </div>
              <div>
                <dt className="text-body-sm font-medium text-slate-500 dark:text-slate-400">
                  Organisation ID
                </dt>
                <dd className="text-body-sm text-slate-900 dark:text-slate-50 font-mono">
                  {profile?.organisationId ||
                    user?.organisationId ||
                    'Not assigned'}
                </dd>
              </div>
            </dl>
            {profileSuccess && (
              <Alert variant="success">Profile updated successfully.</Alert>
            )}
            {profileError && (
              <Alert variant="error">{profileError}</Alert>
            )}
            <Button
              aria-label="Save Profile"
              disabled={profileSaveDisabled}
              onClick={handleProfileSave}
            >
              {updateProfileMutation.isPending ? 'Saving...' : 'Save Profile'}
            </Button>
          </div>
        )}
      </Card>

      {/* Security */}
      <Card>
        <h2 className="text-h3 text-slate-900 dark:text-slate-50 mb-4">
          Security
        </h2>
        <div className="space-y-4">
          <div>
            <label htmlFor="current-password" className="label">
              Current Password
            </label>
            <input
              id="current-password"
              type="password"
              className="input mt-1"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                setPasswordSuccess(false);
                setPasswordError('');
              }}
              autoComplete="current-password"
            />
          </div>
          <div>
            <label htmlFor="new-password" className="label">
              New Password
            </label>
            <input
              id="new-password"
              type="password"
              className="input mt-1"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setPasswordSuccess(false);
                setPasswordError('');
              }}
              autoComplete="new-password"
            />
            <p className="text-caption text-slate-500 dark:text-slate-400 mt-1">
              At least 12 characters, including uppercase, lowercase, number,
              and symbol.
            </p>
          </div>
          <div>
            <label htmlFor="confirm-password" className="label">
              Confirm New Password
            </label>
            <input
              id="confirm-password"
              type="password"
              className="input mt-1"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setPasswordSuccess(false);
                setPasswordError('');
              }}
              autoComplete="new-password"
            />
          </div>
          {passwordSuccess && (
            <Alert variant="success">Password changed successfully.</Alert>
          )}
          {passwordError && (
            <Alert variant="error">{passwordError}</Alert>
          )}
          <Button
            aria-label="Change Password"
            disabled={
              !currentPassword ||
              !newPassword ||
              !confirmPassword ||
              passwordChanging
            }
            onClick={handlePasswordChange}
          >
            {passwordChanging ? 'Changing...' : 'Change Password'}
          </Button>
        </div>
      </Card>

      {/* Multi-Factor Authentication */}
      <Card>
        <h2 className="text-h3 text-slate-900 dark:text-slate-50 mb-4">
          Multi-Factor Authentication
        </h2>
        <div className="space-y-4">
          {mfaSuccess && <Alert variant="success">{mfaSuccess}</Alert>}
          {mfaError && <Alert variant="error">{mfaError}</Alert>}

          {mfaState === 'loading' && (
            <p className="text-body-sm text-slate-500 dark:text-slate-400">
              Loading MFA status...
            </p>
          )}

          {mfaState === 'inactive' && (
            <>
              <p className="text-body-sm text-slate-600 dark:text-slate-400">
                Add an extra layer of security to your account by enabling
                time-based one-time password (TOTP) authentication.
              </p>
              <Button onClick={handleEnableMfa}>Enable MFA</Button>
            </>
          )}

          {mfaState === 'setting-up' && totpSetup && (
            <>
              <p className="text-body-sm text-slate-600 dark:text-slate-400">
                Scan the QR code below with your authenticator app, or enter
                the secret key manually.
              </p>
              <div
                className="flex justify-center p-4 bg-white rounded-lg border border-slate-200 dark:border-slate-600"
                role="img"
                aria-label="QR code for authenticator app setup"
              >
                <QRCodeSVG
                  value={totpSetup.uri}
                  size={160}
                  level="M"
                  data-testid="mfa-qr-code"
                />
              </div>
              <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-lg">
                <p className="text-body-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Secret Key
                </p>
                <code className="text-body-sm text-slate-900 dark:text-slate-50 font-mono break-all">
                  {totpSetup.sharedSecret}
                </code>
              </div>
              <div>
                <label htmlFor="mfa-verify-code" className="label">
                  Verification Code
                </label>
                <input
                  id="mfa-verify-code"
                  type="text"
                  required
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  className="input mt-1"
                  placeholder="123456"
                  autoComplete="one-time-code"
                />
              </div>
              <div className="flex gap-3">
                <Button onClick={handleVerifyTotp}>Verify</Button>
                <Button variant="secondary" onClick={handleCancelMfaSetup}>
                  Cancel
                </Button>
              </div>
            </>
          )}

          {mfaState === 'active' && (
            <>
              <p className="text-body-sm text-slate-600 dark:text-slate-400">
                MFA is currently active on your account.
              </p>
              <Button variant="danger" onClick={handleDisableMfa}>
                Disable MFA
              </Button>
            </>
          )}

          {mfaState === 'confirming-disable' && (
            <>
              <p className="text-body-sm text-slate-600 dark:text-slate-400">
                Are you sure you want to disable multi-factor authentication?
                This will make your account less secure.
              </p>
              <div className="flex gap-3">
                <Button variant="danger" onClick={handleConfirmDisable}>
                  Confirm Disable
                </Button>
                <Button variant="secondary" onClick={handleCancelDisable}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </Card>

      <NotificationPreferencesCard
        notifPrefs={notifPrefs}
        isLoading={notifLoading}
        isError={notifError}
        isPending={updateNotifMutation.isPending}
        allContracts={allContracts}
        onToggleOverdue={handleToggleOverdue}
        onToggleDueSoon={handleToggleDueSoon}
        onToggleInsurance={handleToggleInsurance}
        onRemoveMutedContract={handleRemoveMutedContract}
        onMuteContract={handleMuteContract}
      />

      {/* Account Actions */}
      <Card>
        <h2 className="text-h3 text-slate-900 dark:text-slate-50 mb-4">
          Account
        </h2>
        <Button variant="danger" onClick={logout}>
          Sign Out
        </Button>
      </Card>

      {/* App Info */}
      <Card>
        <h2 className="text-h3 text-slate-900 dark:text-slate-50 mb-4">
          About
        </h2>
        <dl className="space-y-2 text-body-sm">
          <div className="flex justify-between">
            <dt className="font-medium text-slate-500 dark:text-slate-400">
              Version
            </dt>
            <dd className="text-slate-900 dark:text-slate-50">
              {__APP_VERSION__}
              {__GIT_SHA__ && ` (${__GIT_SHA__})`}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="font-medium text-slate-500 dark:text-slate-400">
              Environment
            </dt>
            <dd className="text-slate-900 dark:text-slate-50">
              {import.meta.env.VITE_APP_ENVIRONMENT}
            </dd>
          </div>
        </dl>
      </Card>
    </div>
  );
}
